import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Check, X, ChevronDown, ChevronUp, ArrowRight, ArrowLeft,
  HelpCircle, AlertTriangle, Loader2, Plus, MessageCircle,
} from 'lucide-react';
import { QualitaetsAnzeige } from './QualitaetsAnzeige';

function WarumToggle({ warum, warumWichtig, t }) {
  const [open, setOpen] = useState(false);
  if (!warum) return null;
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="text-[11px] text-primary/70 hover:text-primary flex items-center gap-1 mt-1 transition-colors"
        data-testid="warum-toggle"
      >
        <HelpCircle className="h-3 w-3" />
        {open ? t('fa.erklaerungAusblenden') : t('fa.warum')}
      </button>
      {open && (
        <div className="mt-1.5 p-2.5 rounded-md bg-warm-50 border border-warm-200 text-xs text-foreground leading-relaxed" data-testid="warum-erklaerung">
          <p>{warum}</p>
          {warumWichtig && <p className="mt-1.5 text-muted-foreground">{warumWichtig}</p>}
        </div>
      )}
    </div>
  );
}

function OffenerPunktInline({ punkt, antworten, onAntwort, t }) {
  const [custom, setCustom] = useState('');
  const [showWarum, setShowWarum] = useState(false);
  const aktuelleAntwort = antworten[punkt.feld];

  return (
    <div className="p-3.5 rounded-lg border border-amber-200 bg-amber-50/50" data-testid={`offener-punkt-${punkt.feld}`}>
      <div className="flex items-start gap-2 mb-2">
        <HelpCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{punkt.frage}</p>
          <button
            onClick={() => setShowWarum(!showWarum)}
            className="text-[11px] text-amber-700/70 hover:text-amber-700 flex items-center gap-1 mt-0.5"
          >
            <HelpCircle className="h-2.5 w-2.5" />
            {showWarum ? t('fa.ausblenden') : t('fa.warumWichtig')}
          </button>
          {showWarum && (
            <div className="mt-1.5 text-xs text-amber-800/70 leading-relaxed">
              <p>{punkt.warum_offen}</p>
              <p className="mt-1">{punkt.warum_wichtig}</p>
            </div>
          )}
        </div>
      </div>

      {punkt.eingabe_typ === 'auswahl' && punkt.optionen?.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {punkt.optionen.map((opt, i) => (
            <button
              key={i}
              onClick={() => onAntwort(punkt.feld, opt)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-all ${aktuelleAntwort === opt ? 'bg-primary text-white border-primary' : 'bg-white border-warm-200 text-foreground hover:border-primary/40 hover:bg-primary/5'}`}
              data-testid={`option-${punkt.feld}-${i}`}
            >
              {opt}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && custom.trim() && onAntwort(punkt.feld, custom.trim())}
            placeholder={t('fa.ihreAngabe')}
            className="flex-1 h-8 px-3 text-xs rounded-md border border-warm-200 bg-white focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
          <Button size="sm" variant="ghost" onClick={() => custom.trim() && onAntwort(punkt.feld, custom.trim())} className="h-8 px-2">
            <Check className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
      {aktuelleAntwort && (
        <div className="mt-2 flex items-center gap-1.5">
          <Check className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-xs text-emerald-700 font-medium">{aktuelleAntwort}</span>
          <button onClick={() => onAntwort(punkt.feld, null)} className="text-[10px] text-muted-foreground ml-1 hover:text-foreground">{t('fa.aendern')}</button>
        </div>
      )}
    </div>
  );
}

function NebenthemaKarte({ thema, t }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/30" data-testid={`nebenthema-${thema.typ}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs border-blue-200 text-blue-700 bg-blue-50">
            {thema.label}
          </Badge>
          <span className="text-[11px] text-muted-foreground">{t('fa.nebenthema')}</span>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {expanded ? t('fa.weniger') : t('fa.details')}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 text-xs text-foreground leading-relaxed" data-testid={`nebenthema-detail-${thema.typ}`}>
          <p>{thema.warum}</p>
          <p className="mt-1.5 text-muted-foreground">{t('fa.nebenthemaErklaerung')}</p>
        </div>
      )}
    </div>
  );
}

function WiderspruchKarte({ widerspruch, onLoesen, t }) {
  const [text, setText] = useState('');
  return (
    <div className="p-3.5 rounded-lg border border-red-200 bg-red-50/50" data-testid="widerspruch-karte">
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{widerspruch.text}</p>
          <p className="text-xs text-red-700/70 mt-0.5">{widerspruch.warum}</p>
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && text.trim() && onLoesen(text.trim())}
          placeholder={t('fa.richtigstellung')}
          className="flex-1 h-8 px-3 text-xs rounded-md border border-red-200 bg-white focus:outline-none focus:ring-1 focus:ring-red-300"
          data-testid="widerspruch-input"
        />
        <Button size="sm" variant="outline" onClick={() => text.trim() && onLoesen(text.trim())} className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50">
          {t('fa.klaeren')}
        </Button>
      </div>
    </div>
  );
}

export function StepErkannt({ ansicht, onWeiter, onErgaenzen, onZurueck, loading, t }) {
  const [antworten, setAntworten] = useState({});
  const [freitext, setFreitext] = useState('');
  const [showFreitext, setShowFreitext] = useState(false);

  const erkannt = ansicht?.erkannt || {};
  const merkmale = erkannt.erkannte_merkmale || [];
  const offenePunkte = erkannt.offene_punkte || [];
  const nebenthemen = erkannt.nebenthemen || [];
  const widersprueche = erkannt.widersprueche || [];
  const rueckfragen = erkannt.zielgruppen_rueckfragen || [];
  const erkannteM = merkmale.filter(m => m.erkannt);
  const fehlendeM = merkmale.filter(m => !m.erkannt);

  const handleAntwort = (feld, wert) => {
    setAntworten(prev => {
      if (wert === null) {
        const copy = { ...prev };
        delete copy[feld];
        return copy;
      }
      return { ...prev, [feld]: wert };
    });
  };

  const handleErgaenzen = () => {
    const teile = [];
    for (const [feld, wert] of Object.entries(antworten)) {
      teile.push(wert);
    }
    if (freitext.trim()) {
      teile.push(freitext.trim());
    }
    if (teile.length > 0) {
      onErgaenzen(teile.join('. '));
      setAntworten({});
      setFreitext('');
      setShowFreitext(false);
    }
  };

  const hatErgaenzungen = Object.keys(antworten).length > 0 || freitext.trim().length > 0;

  return (
    <div className="space-y-4">
      {/* Qualitätsanzeige */}
      <QualitaetsAnzeige
        ansicht={ansicht}
        onZurueckZurEingabe={onZurueck}
        t={t}
      />

      {/* Zusammenfassung */}
      <Card className="border-warm-200 shadow-soft">
        <CardContent className="pt-5 pb-5">
          <h2 className="font-serif text-lg font-bold text-foreground mb-3" data-testid="erkannt-titel">
            {t('fa.erkanntTitel')}
          </h2>
          <p className="text-sm text-foreground leading-relaxed mb-4" data-testid="zusammenfassung-text">
            {erkannt.zusammenfassung_text}
          </p>

          {/* Erkannte Merkmale mit Warum */}
          {erkannteM.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">{t('fa.erkannteMerkmale')}</p>
              <div className="space-y-2" data-testid="erkannte-merkmale">
                {erkannteM.map((m, i) => (
                  <div key={i} className="flex flex-col p-2.5 rounded-md bg-emerald-50/50 border border-emerald-100">
                    <div className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span className="text-xs font-medium text-emerald-800">{m.label}:</span>
                      <span className="text-xs text-foreground">{m.wert}</span>
                    </div>
                    <WarumToggle warum={m.warum} t={t} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fehlende Merkmale */}
          {fehlendeM.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                {t('fa.fehlendeAngaben')}
              </p>
              <div className="space-y-2">
                {fehlendeM.map((m, i) => (
                  <div key={i} className="flex flex-col p-2.5 rounded-md bg-warm-50/50 border border-warm-200">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs font-medium text-muted-foreground">{m.label}:</span>
                      <span className="text-xs text-muted-foreground">{m.wert}</span>
                    </div>
                    <WarumToggle warum={m.warum} warumWichtig={m.warum_wichtig} t={t} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nebenthemen */}
      {nebenthemen.length > 0 && (
        <Card className="border-blue-100 shadow-soft" data-testid="nebenthemen-section">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-semibold text-foreground">{t('fa.weitereThemen')}</h3>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-200 text-blue-600">
                {nebenthemen.length}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {t('fa.nebenHauptthemaPrefix')} &quot;{erkannt.hauptthema?.label}&quot; {t('fa.nebenHauptthemaSuffix')}
            </p>
            <div className="space-y-2">
              {nebenthemen.map((nt, i) => (
                <NebenthemaKarte key={i} thema={nt} t={t} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Widersprüche */}
      {widersprueche.length > 0 && (
        <Card className="border-red-100 shadow-soft" data-testid="widersprueche-section">
          <CardContent className="pt-5 pb-5">
            <h3 className="text-sm font-semibold text-foreground mb-3">{t('fa.widersprueche')}</h3>
            <div className="space-y-2">
              {widersprueche.map((w, i) => (
                <WiderspruchKarte key={i} widerspruch={w} onLoesen={(text) => {
                  setFreitext(prev => prev ? `${prev}. ${text}` : text);
                  setShowFreitext(true);
                }} t={t} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Beantwortbare offene Punkte */}
      {offenePunkte.length > 0 && (
        <Card className="border-amber-100 shadow-soft" data-testid="offene-punkte-section">
          <CardContent className="pt-5 pb-5">
            <h3 className="text-sm font-semibold text-foreground mb-1">{t('fa.offenePunkteKlaeren')}</h3>
            <p className="text-xs text-muted-foreground mb-4">
              {t('fa.offenePunkteFehlen')}
            </p>
            <div className="space-y-3" data-testid="offene-punkte-liste">
              {offenePunkte.map((punkt, i) => (
                <OffenerPunktInline
                  key={punkt.feld}
                  punkt={punkt}
                  antworten={antworten}
                  onAntwort={handleAntwort}
                  t={t}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zielgruppen-Rückfragen */}
      {rueckfragen.length > 0 && (
        <Card className="border-warm-200 shadow-soft">
          <CardContent className="pt-5 pb-5">
            <h3 className="text-sm font-semibold text-foreground mb-3">{t('fa.rueckfragen')}</h3>
            <div className="space-y-2">
              {rueckfragen.map((rf, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-md bg-warm-50 border border-warm-200">
                  <MessageCircle className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                  <p className="text-xs text-foreground leading-relaxed">{rf}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Freitext-Ergänzung */}
      <Card className="border-warm-200 shadow-soft">
        <CardContent className="pt-5 pb-5">
          {!showFreitext ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFreitext(true)}
              className="w-full border-dashed border-warm-300 text-muted-foreground hover:text-primary hover:border-primary/30"
              data-testid="freitext-ergaenzen-toggle"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              {t('fa.freitextHinzufuegen')}
            </Button>
          ) : (
            <div>
              <p className="text-xs font-medium text-foreground mb-2">{t('fa.weitereInfos')}</p>
              <Textarea
                value={freitext}
                onChange={(e) => setFreitext(e.target.value)}
                placeholder={t('fa.freitextPlaceholder')}
                className="min-h-[80px] text-sm resize-none"
                data-testid="freitext-ergaenzen-input"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aktions-Buttons */}
      <div className="flex items-center gap-3 pt-2">
        {onZurueck && (
          <Button variant="outline" onClick={onZurueck} data-testid="zurueck-btn-2">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('schnellcheck.zurueck')}
          </Button>
        )}

        {hatErgaenzungen ? (
          <Button
            onClick={handleErgaenzen}
            disabled={loading}
            className="flex-1 h-11"
            data-testid="ergaenzen-btn"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('schnellcheck.wirdAnalysiert')}</>
            ) : (
              <><Check className="h-4 w-4 mr-2" /> {t('schnellcheck.weiter')}</>
            )}
          </Button>
        ) : (
          <Button
            onClick={onWeiter}
            className="flex-1 h-11"
            data-testid="stimmt-so-btn"
          >
            {t('schnellcheck.weiter')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
